// WARNING: If you add imports here these will also impact sub repositories (cloudRun, functions, etc), you might need to add imports there as well.
import { BusinessGroup, FieldType, Meta } from './System.types';
import type { Timestamp } from "firebase/firestore";
import validator from 'validator';

/**
 * RFC 5322 compliant email validation regex.
 * Handles most valid email formats including:
 * - Standard formats (user@domain.com)
 * - Subdomains (user@mail.domain.com)
 * - Plus addressing (user+tag@domain.com)
 * - Numeric domains (user@123.123.123.123)
 * - Hyphens in domain (user@my-domain.com)
 */
export const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/** Maximum allowed email length per RFC 5321 */
export const EMAIL_MAX_LENGTH = 254;

/**
 * Backs the `time`/`date`/`dateTime` field types and, per endpoint, the three range types.
 * Every date carrier the system persists or edits — a web-SDK `Timestamp`, an admin-SDK
 * `Timestamp`, a `Dayjs` — exposes `toDate`, so the `toDate` branch answers for all of them
 * and no SDK-specific `instanceof` is needed; importing one for that would drag the web SDK
 * into the server graph. A raw `{seconds}`/`{_seconds}` payload has no `toDate` and is accepted
 * on shape alone. Empty is valid; required-ness is a separate check.
 */
const validateDate = (value: any): boolean => {
    if (!value) return true;
    if (typeof value === 'object' && value !== null) {
        const maybe: any = value;

        if (typeof maybe.toDate === 'function') {
            const d = maybe.toDate();
            return d instanceof Date && !isNaN(d.getTime());
        }

        const seconds =
            typeof maybe.seconds === 'number'
                ? maybe.seconds
                : typeof maybe._seconds === 'number'
                    ? maybe._seconds
                    : null;

        if (seconds !== null) return true;
    }
    if (value instanceof Date) return !isNaN(value.getTime());
    return false;
};



/**
 * Represents the foundational field data types in the system.
 * These are used to build the built-in data types, and the org-level-config field types.
 */
export type SystemFieldTypeId =
    | 'text'
    | 'shortText'
    | 'longText'
    | 'richText'
    | 'percentage'
    | 'currency'
    | 'quantity'
    | 'number'
    | 'yesNo'
    | 'time'
    | 'date'
    | 'dateTime'
    | 'timeRange'
    | 'dateRange'
    | 'dateTimeRange'
    | 'multiSelect'
    | 'singleSelect'
    | 'address'
    | 'null'
    | 'email'
    | 'phone'
    | 'sellPriceUI'
    | 'url'
    | 'ipAddress'
    | 'json'
    | 'xml'
    | 'html'
    | 'password'
    | 'graph'
    | 'emailList'
    | 'communicationLog'
    | 'formBanner'
    | 'relationshipTree'
    | 'shippingTrackerCompact'
    | 'shippingTrackerExpanded'
    | 'qos'
    | 'composite'
    | 'userAndGroup';

/**
 * Represents a time range with start and end timestamps.
 * Used for dateRange, timeRange, and dateTimeRange field types.
 */
export type TimeRange = {
  start: Timestamp;
  end: Timestamp;
};

/**
 * Represents the foundational field data types in the system.
 * These are used to build the built-in data types, and the org-level-config field types.
 */
const communicationLogFieldType: FieldType = {
    fieldDataTypeId: 'communicationLog',
    description: {
        shortLabel: 'Communication Log',
        shortDescription: 'Emails and phone calls',
        longLabel: 'Communication Log Field',
        longDescription: 'Tracks sent/received emails and phone calls with unified tagging and attachments.',
    },
    errorMessage: 'Invalid communication log entry.',
    columns: [
        {
            dataIndex: 'type',
            title: 'Type',
            width: 90,
        },
        {
            dataIndex: 'timestamp',
            title: 'Date',
            width: 170,
        },
        {
            dataIndex: 'tag',
            title: 'Tag',
            width: 150,
        },
        {
            dataIndex: 'from',
            title: 'From / Direction',
            width: 220,
        },
        {
            dataIndex: 'subject',
            title: 'Subject / Notes',
            width: 320,
        },
        {
            dataIndex: 'hasAttachments',
            title: 'Attachments',
            width: 120,
        },
    ],
    editorConfig: {
        showDefaultValue: false,
        showMinMax: false,
    },
};

export const NUMERIC_FIELD_DATA_TYPES: ReadonlySet<string> = new Set<SystemFieldTypeId>([
    'number', 'currency', 'percentage', 'quantity',
]);

/**
 * The scalar date-family types the write-time coercer canonicalizes to a JS `Date`
 * via `coerceCellDate`. The `{start,end}` range types (`timeRange`/`dateRange`/
 * `dateTimeRange`) are intentionally excluded — different output shape, deferred.
 */
export const DATE_FIELD_DATA_TYPES: ReadonlySet<string> = new Set<SystemFieldTypeId>([
    'date', 'dateTime', 'time',
]);

/**
 * The range date-family types the write-time coercer canonicalizes to a
 * `{ start, end }` pair via `coerceCellRange`. Kept as a SEPARATE set from
 * `DATE_FIELD_DATA_TYPES` on purpose: the output shape differs (a range, not a
 * scalar `Date`), so the two families dispatch to different coercers.
 */
export const RANGE_FIELD_DATA_TYPES: ReadonlySet<string> = new Set<SystemFieldTypeId>([
    'timeRange', 'dateRange', 'dateTimeRange',
]);

/**
 * The field types whose value is a calendar day rather than an instant: `date`
 * and `dateRange`. They store start of day in `defaultTimeZone` (SYD-2507);
 * `dateTime`, `time` and their ranges store the exact instant the user picked.
 * Cuts across `DATE_FIELD_DATA_TYPES` and `RANGE_FIELD_DATA_TYPES` on purpose:
 * those split by output shape, this one splits by anchor.
 */
export const DATE_ONLY_FIELD_DATA_TYPES: ReadonlySet<string> = new Set<SystemFieldTypeId>([
    'date', 'dateRange',
]);

/**
 * The field types `ControlUI` renders in an `Input.TextArea`, so a clipboard
 * paste keeps the whole multi-line payload instead of the first line only
 * (SYD-2232). It mirrors that one `switch` case list and nothing else: `ipAddress`
 * is single-line semantically but shares the TextArea branch, and mirroring the
 * control is what makes the rule checkable against the code it speaks for.
 */
export const MULTILINE_TEXT_FIELD_DATA_TYPES: ReadonlySet<string> = new Set<SystemFieldTypeId>([
    'longText', 'richText', 'json', 'xml', 'html', 'ipAddress',
]);

export const fieldDataTypes: Record<SystemFieldTypeId, FieldType> = {
    shortText: {
        fieldDataTypeId: 'shortText',
        description: {
            shortLabel: 'Short Text',
            shortDescription: 'A single line of text',
            longLabel: 'Short Text Field',
            longDescription: 'Stores a single line of text, suitable for names, titles, or short descriptions.'
        },
        errorMessage: `Must be a valid text string`,
    },
    text: {
        fieldDataTypeId: 'text',
        description: {
            shortLabel: 'Text',
            shortDescription: 'A single line of text',
            longLabel: 'Text Field',
            longDescription: 'Stores a single line of text, suitable for names, titles, or short descriptions.'
        },
        errorMessage: `Must be a valid text string`,
        editorConfig: {
            showPrefix: true,
            showSuffix: true,
        }
    },
    longText: {
        fieldDataTypeId: 'longText',
        description: {
            shortLabel: 'Long Text',
            shortDescription: 'Multiple lines of text',
            longLabel: 'Long Text Field',
            longDescription: 'Stores multiple lines of text, suitable for longer descriptions, comments, or notes.'
        },
        errorMessage: `Must be a valid text string`,
        editorConfig: {}
    },
    richText: {
        fieldDataTypeId: 'richText',
        description: {
            shortLabel: 'Rich Text',
            shortDescription: 'Formatted text',
            longLabel: 'Rich Text Field',
            longDescription: 'Stores formatted text with styling and potentially embedded media.'
        },
        errorMessage: `Must be a valid text string`,
        editorConfig: {
            showMinMax: false,
        }
    },
    percentage: {
        fieldDataTypeId: 'percentage',
        aggregatable: true,
        description: {
            shortLabel: 'Percentage',
            shortDescription: 'Percentage value',
            longLabel: 'Percentage Field',
            longDescription: 'Stores a percentage value.'
        },
        validation: (value: any) => !value || (typeof value === 'number' && value >= -1000 && value <= 1000),
        errorMessage: 'Input must be between -1000 and 1000',
        formatter: (value: any) => {
            if (!value) return '';
            const num = Number(value);
            const hasDecimals = num % 1 !== 0;
            const formattedValue = hasDecimals ? num.toFixed(1) : num.toString();
            return formattedValue;
        },
        parser: (value: string | undefined) => value ? value.replace(/%/g, '') : undefined,
        prefix: '%',
        editorConfig: {
            showPrefix: true,
            showSuffix: true,
        }
    },
    currency: {
        fieldDataTypeId: 'currency',
        aggregatable: true,
        description: {
            shortLabel: 'Currency',
            shortDescription: 'Monetary value',
            longLabel: 'Currency Field',
            longDescription: 'Stores a monetary value with currency information.'
        },
        validation: (value: any) => !value || (typeof value === 'number' && value >= -1000000000 && value <= 1000000000),
        errorMessage: 'Input must be between -1,000,000,000 and 1,000,000,000',
        formatter: (value: any) => {
            if (!value) return '';
            const num = Number(value);
            const hasDecimals = num % 1 !== 0;
            const formattedValue = hasDecimals ? num.toFixed(2) : num.toString();
            return formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        parser: (value: string | undefined) => value ? parseFloat(value.replace(/[$,]/g, '')) : undefined,
        prefix: '$',
        editorConfig: {
            showPrefix: true,
            showSuffix: true,
        }
    },
    quantity: {
        fieldDataTypeId: 'quantity',
        aggregatable: true,
        description: {
            shortLabel: 'Quantity',
            shortDescription: 'Numeric quantity',
            longLabel: 'Quantity Field',
            longDescription: 'Stores a numeric quantity, typically used for inventory or measurements.'
        },
        validation: (value: any) => !value || (typeof value === 'number' && value >= 0),
        errorMessage: 'Must be a positive number',
        formatter: (value: any) => value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
        parser: (value: string | undefined) => value ? value.replace(/,/g, '') : undefined,
        editorConfig: {
            showPrefix: true,
            showSuffix: true,
        }
    },
    number: {
        fieldDataTypeId: 'number',
        aggregatable: true,
        description: {
            shortLabel: 'Number',
            shortDescription: 'Numeric value',
            longLabel: 'Number Field',
            longDescription: 'Stores a numeric value, which can be an integer or a decimal number.'
        },
        validation: (value: any) => !value || typeof value === 'number',
        errorMessage: 'Must be a valid number',
        formatter: (value: any) => value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
        parser: (value: string | undefined) => value ? (value.replace(/,/g, '')) : undefined,
        editorConfig: {
            showPrefix: true,
            showSuffix: true,
        }
    },
    yesNo: {
        fieldDataTypeId: 'yesNo',
        description: {
            shortLabel: 'Yes/No',
            shortDescription: 'Boolean value',
            longLabel: 'Yes/No Field',
            longDescription: 'Stores a boolean value, typically represented as Yes or No.'
        },
        validation: (value: any) => typeof value === 'boolean',
        errorMessage: 'Must be true or false',
        editorConfig: {
            showMinMax: false,
        }
    },
    time: {
        fieldDataTypeId: 'time',
        description: {
            shortLabel: 'Time',
            shortDescription: 'Time value',
            longLabel: 'Time Field',
            longDescription: 'Stores a time value without date information.'
        },
        validation: validateDate,
        errorMessage: 'Must be a valid time',
        editorConfig: {}
    },
    date: {
        fieldDataTypeId: 'date',
        description: {
            shortLabel: 'Date',
            shortDescription: 'Date value',
            longLabel: 'Date Field',
            longDescription: 'Stores a date value without time information.'
        },
        validation: validateDate,
        errorMessage: 'Must be a valid date',
        editorConfig: {}
    },
    dateTime: {
        fieldDataTypeId: 'dateTime',
        description: {
            shortLabel: 'Date & Time',
            shortDescription: 'Date and time value',
            longLabel: 'Date and Time Field',
            longDescription: 'Stores a date and time value, including timezone information.'
        },
        validation: validateDate,
        errorMessage: 'Must be a valid date and time',
        editorConfig: {}
    },
    timeRange: {
        fieldDataTypeId: 'timeRange',
        description: {
            shortLabel: 'Time Range',
            shortDescription: 'Time period',
            longLabel: 'Time Range Field',
            longDescription: 'Stores a time range with start and end times.'
        },
        validation: (value: any) => {
            if (!value) return true;
            return validateDate(value.start) && validateDate(value.end);
        },
        errorMessage: 'Must have valid start and end times',
        editorConfig: {}
    },
    dateRange: {
        fieldDataTypeId: 'dateRange',
        description: {
            shortLabel: 'Date Range',
            shortDescription: 'Date period',
            longLabel: 'Date Range Field',
            longDescription: 'Stores a date range with start and end dates.'
        },
        validation: (value: any) => {
            if (!value) return true;
            return validateDate(value.start) && validateDate(value.end);
        },
        errorMessage: 'Must have valid start and end dates',
        editorConfig: {}
    },
    dateTimeRange: {
        fieldDataTypeId: 'dateTimeRange',
        description: {
            shortLabel: 'Date & Time Range',
            shortDescription: 'Date and time period',
            longLabel: 'Date & Time Range Field',
            longDescription: 'Stores a date and time range with start and end date-times.'
        },
        validation: (value: any) => {
            if (!value) return true;
            return validateDate(value.start) && validateDate(value.end);
        },
        errorMessage: 'Must have valid start and end date-times',
        editorConfig: {
            showDefaultValue: false,
        }
    },
    multiSelect: {
        fieldDataTypeId: 'multiSelect',
        description: {
            shortLabel: 'Multi-Select',
            shortDescription: 'Multiple selections from options',
            longLabel: 'Multi-Select Field',
            longDescription: 'Allows selection of multiple values from a predefined list of options.'
        },
        validation: (value: any) => {
            if (value === null || value === undefined) return true;
            if (!Array.isArray(value)) return false;
            return value.every((v) => typeof v === 'string' || typeof v === 'number');
        },
        errorMessage: 'Must be a list of selected options',
        editorConfig: {
            showFieldOptions: true,
        }
    },
    singleSelect: {
        fieldDataTypeId: 'singleSelect',
        description: {
            shortLabel: 'Single Select',
            shortDescription: 'Single selection from options',
            longLabel: 'Single Select Field',
            longDescription: 'Allows selection of a single value from a predefined list of options.'
        },
        validation: (value: any) =>
            value === null ||
            value === undefined ||
            typeof value === 'string' ||
            typeof value === 'number',
        errorMessage: 'Must select an option',
        editorConfig: {
            showFieldOptions: true,
        }
    },
    address: {
        fieldDataTypeId: 'address',
        description: {
            shortLabel: 'Address',
            shortDescription: 'Street address',
            longLabel: 'Address Field',
            longDescription: 'Stores a street address with multiple components like street, city, state, zip, etc.'
        },
        validation: (value: any) => {
            if (!value) return true;
            if (typeof value === 'string') return true;
            if (typeof value === 'object' && (value.formatted || value.components)) return true;
            return false;
        },
        errorMessage: 'Must be a valid address',
        editorConfig: {}
    },
    null: {
        fieldDataTypeId: 'null',
        description: {
            shortLabel: 'Null',
            shortDescription: 'Empty value',
            longLabel: 'Null Field',
            longDescription: 'Represents an intentionally empty or undefined value.'
        },
        editorConfig: {
            showDefaultValue: false,
            showMinMax: false,
        }
    },
    email: {
        fieldDataTypeId: 'email',
        description: {
            shortLabel: 'Email',
            shortDescription: 'Email address',
            longLabel: 'Email Field',
            longDescription: 'Stores an email address.'
        },
        validation: (value: any) => {
            if (!value) return true;  
            return typeof value === 'string' && validator.isEmail(value);
        },
        errorMessage: 'Please enter a valid email address',
        editorConfig: {}
    },
    phone: {
        fieldDataTypeId: 'phone',
        description: {
            shortLabel: 'Phone',
            shortDescription: 'Phone number',
            longLabel: 'Phone Field',
            longDescription: 'Stores a phone number with international format support.'
        },
        validation: (value: any) => {
            if (!value) return true;
            if (typeof value === 'string') return true;
            if (typeof value === 'object' && (value.formatted || value.components)) return true;
            return false;
        },
        errorMessage: 'Please enter a valid phone number',
        editorConfig: {}
    },
    sellPriceUI: {
        fieldDataTypeId: 'sellPriceUI',
        compound: true,
        aggregatable: true,
        description: {
            shortLabel: 'Sell Price',
            shortDescription: 'Price calculated from base price with markup or markdown rate',
            longLabel: 'Sell Price',
            longDescription: 'A price field that tracks the base price and applies a markup (↑) or markdown (↓) percentage to calculate the final price.',
        },
        validation: (value: any) => {
            if (!value) return true;
            if (typeof value === 'number') return Number.isFinite(value);
            if (typeof value === 'object' && 'fields' in value) return true;
            return false;
        },
        errorMessage: 'Must be a valid sell price value',
        editorConfig: {}
    },
    url: {
        fieldDataTypeId: 'url',
        description: {
            shortLabel: 'URL',
            shortDescription: 'Web address',
            longLabel: 'URL Field',
            longDescription: 'Stores a web address or URL link.'
        },
        validation: (value: any) => {
            if (!value) return true;  
            return typeof value === 'string' && validator.isURL(value);
        },
        errorMessage: 'Must be a valid URL',
        formatter: (value: any) => value || '',
        parser: (value: string | undefined) => {
            if (!value) return undefined;
            return value.startsWith('http://') || value.startsWith('https://')
                ? value
                : `https://${value}`;
        },
        editorConfig: {
            showSuffix: true,
        }
    },
    ipAddress: {
        fieldDataTypeId: 'ipAddress',
        description: {
            shortLabel: 'IP Address',
            shortDescription: 'Internet Protocol address',
            longLabel: 'IP Address Field',
            longDescription: 'Stores an IP address (IPv4 or IPv6).'
        },
        validation: (value: any) => {
            if (!value) return true;  
            return typeof value === 'string' && validator.isIP(value);
        },
        errorMessage: 'Must be a valid IPv4 or IPv6 address',
        editorConfig: {}
    },
    json: {
        fieldDataTypeId: 'json',
        description: {
            shortLabel: 'JSON',
            shortDescription: 'JSON data',
            longLabel: 'JSON Field',
            longDescription: 'Stores structured data in JSON format.'
        },
        validation: (value: any) => {
            if (!value) return true;  
            return typeof value === 'string' && validator.isJSON(value);
        },
        errorMessage: 'Must be valid JSON',
        editorConfig: {
            showMinMax: false,
        }
    },
    xml: {
        fieldDataTypeId: 'xml',
        description: {
            shortLabel: 'XML',
            shortDescription: 'XML data',
            longLabel: 'XML Field',
            longDescription: 'Stores structured data in XML format.'
        },
        editorConfig: {
            showMinMax: false,
        }
    },
    html: {
        fieldDataTypeId: 'html',
        description: {
            shortLabel: 'HTML',
            shortDescription: 'HTML content',
            longLabel: 'HTML Field',
            longDescription: 'Stores HTML content for rich text formatting.'
        },
        editorConfig: {
            showMinMax: false,
        }
    },
    password: {
        fieldDataTypeId: 'password',
        description: {
            shortLabel: 'Password',
            shortDescription: 'Secure password',
            longLabel: 'Password Field',
            longDescription: 'Stores a password securely, typically with masking and encryption.'
        },
        validation: (value: any) => {
            if (!value) return true;  
            return typeof value === 'string' && validator.isStrongPassword(value, {
                minLength: 8,
                minLowercase: 1,
                minUppercase: 1,
            });
        },
        errorMessage: 'Password must be at least 8 characters long and include at least: 1 lowercase letter, 1 uppercase letter',
        editorConfig: {
            showDefaultValue: false,
            showMinMax: false,
        }
    },
    graph: {
        fieldDataTypeId: 'graph',
        description: {
            shortLabel: 'Graph',
            shortDescription: 'Graph represents relationships.',
            longLabel: 'Graph Builder',
            longDescription: 'Graph represents relationships between different elements.'
        },
        editorConfig: {
            showDefaultValue: false,
            showMinMax: false,
        }
    },
    emailList: {
        ...communicationLogFieldType,
        description: {
            shortLabel: 'Email List',
            shortDescription: 'Legacy email log',
            longLabel: 'Email List Field (Communication Log Alias)',
            longDescription: 'Legacy alias for Communication Log. Stores emails and calls with tagging and attachments.',
        },
    },
    communicationLog: communicationLogFieldType,
    formBanner: {
        fieldDataTypeId: 'formBanner',
        description: {
            shortLabel: 'Banner',
            shortDescription: 'Banner with selectable messages and colors',
            longLabel: 'Form Banner',
            longDescription: 'Displays a banner with different messages and colors that can be selected from a dropdown.'
        },
        editorConfig: {
            showFieldOptions: true,
        }
    },
    relationshipTree: {
        fieldDataTypeId: 'relationshipTree',
        description: {
            shortLabel: 'Relationship Tree',
            shortDescription: 'Configurable hierarchical relationship tree',
            longLabel: 'Relationship Tree',
            longDescription: 'A generic hierarchical tree. Variants are declared as custom field types in parameters/customFieldTypes; the per-variant tree config (allowed children, entity ids, depth, etc.) lives under props.config.'
        },
        editorConfig: {
            showDefaultValue: false,
            showMinMax: false,
        }
    },
    shippingTrackerCompact: {
        fieldDataTypeId: 'shippingTrackerCompact',
        description: {
            shortLabel: 'Shipping Tracker (Compact)',
            shortDescription: 'Compact shipment tracking with progress indicator',
            longLabel: 'Shipping Tracker - Compact View',
            longDescription: 'Displays shipment tracking in a compact format showing progress bar, delivery date, and expandable details.'
        },
        validation: (value: any) => {
            if (!value) return true;
            if (typeof value === 'string') return true;
            if (typeof value === 'object' && value.components?.trackingNumber) return true;
            return false;
        },
        errorMessage: 'Must be valid tracking data',
        editorConfig: {}
    },
    shippingTrackerExpanded: {
        fieldDataTypeId: 'shippingTrackerExpanded',
        description: {
            shortLabel: 'Shipping Tracker (Expanded)',
            shortDescription: 'Full shipment tracking with complete history',
            longLabel: 'Shipping Tracker - Expanded View',
            longDescription: 'Displays complete shipment tracking information including full history, locations, and detailed timeline.'
        },
        validation: (value: any) => {
            if (!value) return true;
            if (typeof value === 'string') return true;
            if (typeof value === 'object' && value.components?.trackingNumber) return true;
            return false;
        },
        errorMessage: 'Must be valid tracking data',
        editorConfig: {
            showDefaultValue: false,
            showMinMax: false,
        }
    },
    qos: {
        fieldDataTypeId: 'qos',
        description: {
            shortLabel: 'QoS',
            shortDescription: '',
            longLabel: '',
            longDescription: 'Quality of Specification'
        },
        editorConfig: {}
    },
    composite: {
        fieldDataTypeId: 'composite',
        compound: true,
        aggregatable: true,
        description: {
            shortLabel: 'Composite Field',
            shortDescription: 'Group of sub-fields',
            longLabel: 'Composite Field',
            longDescription: 'A user-configurable group of sub-fields rendered from a CompositeFieldType layout.',
        },
        editorConfig: {
            showDefaultValue: false,
            showMinMax: false,
        },
    },
    userAndGroup: {
        fieldDataTypeId: 'userAndGroup',
        description: {
            shortLabel: 'User & Group',
            shortDescription: 'Select users and groups',
            longLabel: 'User & Group Selector',
            longDescription: 'Select internal users and/or business groups from the organization directory. Variants are declared as custom field types in parameters/customFieldTypes; the per-variant config (selectionMode, multiSelect, restrictToBusinessGroupIds, enableWeights, maxSelections) lives under props and is typed by UserAndGroupFieldTypeProps.',
        },
        validation: (value: any) => {
            if (!value) return true;
            if (typeof value === 'string') return true;
            if (typeof value === 'object' && (value.formatted !== undefined || value.components)) return true;
            return false;
        },
        errorMessage: 'Must be a valid user or group selection',
        editorConfig: {
            showDefaultValue: false,
            showMinMax: false,
        },
    },
};

/**
 * Represents a structured price value with both formatted and component-based storage.
 * Supports markup/markdown calculations and preset-based labeling.
 */
export type CompositePriceValue = {
    /** Formatted price string for display (e.g., "$1,000.00 (20.00% ↑)") */
    formatted?: string;
    /** Structured price components */
    components?: {
        /** The calculated / final price */
        price?: number;
        /** Reference base price for recalculation */
        basePrice?: number;
        /** Markup/markdown percent (e.g., 20 for 20%) */
        ratePercent?: number;
        /** -1 for markup (↑), 1 for markdown (↓) */
        rateSign?: number;
        /** Preset identifier */
        presetId?: string;
    };
    /** Optional alternative representations */
    alternativeData?: Record<string, any>;
};

/**
 * Represents a shipping destination: who receives it, where it goes, and how to reach them.
 * The component set follows EasyPost's `Address` — the carrier-industry baseline and a superset of
 * Signify's `OrderAddress` — under names that do not assume a US sender. Derivations over this type
 * (formatting, country classification, contact folding) live in `System.StreetAddress.types`.
 */
export type StreetAddress = {
    /** Formatted address string for display, recipient lines first */
    formatted?: string;
    /** Structured address components */
    components?: {
        /** Person the shipment is addressed to */
        recipientName?: string;
        /** Organization at the address */
        company?: string;
        /** Street address line 1. May carry the house number; the two are not decomposed. */
        street?: string;
        /** Street address line 2 (apt, suite, etc.) */
        street2?: string;
        /** City */
        city?: string;
        /** ISO-3166-2 subdivision code, e.g. "IL" */
        state?: string;
        /** ZIP/Postal code */
        postalCode?: string;
        /** ISO-3166-1 alpha-2, e.g. "US". Blank means "not captured", never "domestic". */
        country?: string;
        /** Contact telephone for delivery */
        phone?: string;
        /** Contact email for delivery notifications */
        email?: string;
    };
    /** Optional alternative representations (future: lat/lng, plus codes, etc.) */
    alternativeData?: Record<string, any>;
};

/**
 * Represents a structured telephone value with both formatted and component-based storage.
 * Supports flexible international telephone formats.
 */
export type TelephoneNumber = {
    /** Formatted telephone string for display */
    formatted?: string;
    /** Structured telephone components */
    components?: {
        /** Country calling code (e.g., "+1", "+44") */
        countryCode?: string;
        /** Main telephone number */
        number?: string;
        /** Optional extension */
        extension?: string;
    };
    /** Optional alternative representations (e.g., E.164 format, carrier info) */
    alternativeData?: Record<string, any>;
};

/**
 * Represents shipping tracking information with full history and status updates.
 * Supports both formatted display and structured component data for comprehensive tracking.
 */
export type ShippingTracker = {
    /** Formatted string for display (e.g., "FedEx 123456789 - Delivered on 2025-11-05") */
    formatted?: string;
    /** Structured tracking components */
    components?: {
        /** Tracking number */
        trackingNumber: string;
        /** Carrier identifier (e.g., 'fedex', 'ups', 'usps') */
        carrierId: string;
        /** Estimated or actual delivery date */
        deliveryDate?: Timestamp;
        /** Current shipment status */
        currentStatus?: string;
        /** Full tracking history with timestamps and locations */
        trackingHistory?: Array<{
            /** Timestamp of this tracking event */
            timestamp: Timestamp;
            /** Status at this point (e.g., 'In Transit', 'Out for Delivery', 'Delivered') */
            status: string;
            /** Location description */
            location?: string;
            /** Additional details about this event */
            details?: string;
        }>;
        /** Origin address */
        origin?: string;
        /** Destination address */
        destination?: string;
        /** Additional shipment details (weight, dimensions, etc.) */
        additionalDetails?: Record<string, any>;
    };
    /** Optional metadata for API fetch status, last sync time, etc. */
    meta?: Meta;
};

/**
 * Shape of `FieldType.props` for the `address` base type and for every custom field type declared
 * over it in `parameters/customFieldTypes`. Consumed by `ControlUI` → `AddressUI` → `useAddressUI`.
 * Both flags read as true when absent, so existing field types gain the new lines without edits.
 */
export type AddressFieldTypeProps = {
    /** When false, the recipient and company lines are not offered. */
    showRecipient?: boolean;
    /** When false, the phone and email lines are not offered. */
    showContact?: boolean;
    /**
     * The record sources this field may pull an address from, chosen once by the administrator.
     * Absent or empty: the field searches addresses only.
     */
    recordSources?: AddressRecordSource[];
};

/**
 * One place an address field may pull from: an address-typed property on one entity's records.
 * Resolved when the field type is configured, never by walking layouts at render time.
 */
export type AddressRecordSource = {
    entityId: string;
    /** The address-typed field on that entity's records */
    fieldId: string;
    /** What the person filling the form calls this source, e.g. "Contact address" */
    label: string;
};

export type UserAndGroupSelectionMode =
    | 'usersOnly'
    | 'groupsOnly'
    | 'usersAndGroups'
    | 'cascadeUsersOnly'
    | 'cascadeUsersAndGroups';

export type UserAndGroupSelection = {
    type: 'user' | 'group';
    id: string;
    name?: string;
    /**
     * Relative weight of this selection, 0-100 (e.g. sales credit share).
     * Only meaningful when the field type enables weights
     * (`FieldType.props.enableWeights`); ignored otherwise.
     */
    weightPercentage?: number;
};

/**
 * Shape of `FieldType.props` for the `userAndGroup` base type and for every
 * custom field type declared over it in `parameters/customFieldTypes`.
 * Consumed by `ControlUI` → `UserAndGroupUI` → `useUserAndGroupUI`.
 */
export type UserAndGroupFieldTypeProps = {
    /** Which directory the control offers, and whether it cascades by business group. */
    selectionMode?: UserAndGroupSelectionMode;
    /** Whether more than one selection is allowed. */
    multiSelect?: boolean;
    /** When present, only users belonging to at least one of these business groups are offered. */
    restrictToBusinessGroupIds?: BusinessGroup['docId'][];
    /** When true, each selection carries an editable `weightPercentage` (0-100). */
    enableWeights?: boolean;
    /** Caps selections below the global `MAX_USER_AND_GROUP_SELECTIONS`; never above it. */
    maxSelections?: number;
    /** When true, the selections' `weightPercentage` must sum to `targetWeightTotal`. */
    enforceWeightTotal?: boolean;
    /** Target sum for the weights; read as 100 when absent. Only meaningful with `enforceWeightTotal`. */
    targetWeightTotal?: number;
};

export type UserAndGroupValue = {
    formatted?: string;
    components?: {
        selections: UserAndGroupSelection[];
    };
};
